import { useState } from 'react';
import { Button, Checkbox, Flex, Input, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatStock } from '@app/shared';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { useWindowManager } from '@/shared/lib/windows';
import { CategoryThumb, GroupArchiveButton, useListCategoriesQuery, useSearchCategoriesQuery } from '@/entities/category';
import type { CategoryDto } from '@/entities/category';
import { useSelectableRow } from '@/shared/lib/hooks/useSelectableRow';
import { HotkeyHint } from '@/shared/lib/hotkeys';
import { HotkeyButton } from '@/shared/ui/hotkey';
import { useFocusTarget } from '@/shared/ui/inputs';

export interface CategoryGroupWindowProps {
  id: string;
  onClose: () => void;
}

/** Защитный кап на backend, не пагинация — категорий в одной группе практически его не достигает. */
const SEARCH_LIMIT = 1000;

const COLUMNS: ColumnsType<CategoryDto> = [
  { title: 'Фото', key: 'photo', width: 52, render: (_, c) => <CategoryThumb category={c} /> },
  { title: 'Название', dataIndex: 'name' },
  { title: 'N', dataIndex: 'pcsPerBox', width: 60, align: 'right' },
  { title: 'Опт. цена', dataIndex: 'wholesalePrice', width: 100, align: 'right', render: (v: string | null) => v ?? '—' },
  { title: 'Остаток', width: 140, render: (_, c) => (c.stock ? formatStock(c.stock) : '—') },
  { title: 'Новинка', dataIndex: 'isNew', width: 80, render: (v: boolean) => (v ? <Tag color="blue">новинка</Tag> : null) },
  { title: 'Акция', dataIndex: 'isPromo', width: 80, render: (v: boolean) => (v ? <Tag color="orange">акция</Tag> : null) },
  { title: 'Архив', dataIndex: 'isArchived', width: 70, render: (v: boolean) => (v ? <Tag>архив</Tag> : null) },
];

/** §2.1: плавающее окно группы — основные категории внутри корневой группы `id`, открывается
 *  двойным кликом по строке группы на `CategoriesPage`. Перемещается/растягивается как любое
 *  другое окно (`FloatingWindow`), можно держать несколько групп открытыми одновременно. */
export function CategoryGroupWindow({ id, onClose }: CategoryGroupWindowProps) {
  const windows = useWindowManager();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState('');
  // Всегда с архивными: сама группа может лежать в архиве (заголовок и кнопки не должны пропадать),
  // а флаг «Показать архивные» скрывает только строки подкатегорий.
  const { data = [], isFetching } = useListCategoriesQuery({ includeArchived: true });
  const { attach: attachSearchFilter, focus: focusSearchFilter } = useFocusTarget<HTMLSpanElement>();

  const root = data.find((c) => c.id === id);
  const query = search.trim();
  // Поиск внутри группы — тоже на backend (`categories/search`, parentId: id), не по уже
  // загруженному `data`: без текста поиска список подкатегорий остаётся прежним (клиентский фильтр).
  const { data: matches, isFetching: isSearching } = useSearchCategoriesQuery(
    { q: query || undefined, limit: SEARCH_LIMIT, includeArchived, parentId: id },
    { skip: query === '' },
  );
  const mains =
    query === '' ? data.filter((c) => c.parentId === id && (includeArchived || !c.isArchived)) : matches ?? [];
  const selection = useSelectableRow(mains, {
    onOpen: (item) => windows.open({ kind: 'category', id: item.id }),
    onCreate: () => windows.open({ kind: 'categoryCreate', parentId: id }),
    // В архивную группу подкатегорию не добавить (§2.1) — сначала вернуть группу из архива.
    createDisabled: root?.isArchived ? 'Группа в архиве — сначала верните её из архива' : undefined,
    createLabel: 'Новая категория',
    autoFocus: false, // курсор ставит само окно: `data-autofocus` на таблице ниже
    onFocusFilters: focusSearchFilter,
    onResetFilters: () => setSearch(''),
    filtersEmpty: query === '',
  });

  return (
    <FloatingWindow kind="categoryGroup" title={`Группа: ${root?.name ?? ''}`} onClose={onClose}>
      <Flex vertical gap={8} style={{ height: '100%' }}>
        {root ? (
          // Вход в карточку самой группы: название, фото (загрузка/замена/удаление), архив.
          <Flex gap={12} align="center">
            <CategoryThumb category={root} size={48} previewable />
            <Typography.Text strong>{root.name}</Typography.Text>
            <Button onClick={() => windows.open({ kind: 'category', id })}>Изменить группу</Button>
          </Flex>
        ) : null}

        <Flex justify="space-between" align="center" gap={8} wrap>
          <Space>
            <HotkeyButton type="primary" action={selection.actions.get('list.create')}>
              Новая категория
            </HotkeyButton>
            <HotkeyButton action={selection.actions.get('list.open')}>Открыть</HotkeyButton>
            {root ? <GroupArchiveButton group={root} categories={data} size="middle" onArchived={onClose} /> : null}
            <span ref={attachSearchFilter}>
              <Input
                placeholder="Поиск по названию"
                allowClear
                style={{ width: 200 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </span>
          </Space>
          <Checkbox checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)}>
            Показать архивные
          </Checkbox>
        </Flex>

        <div {...selection.containerProps} data-autofocus>
          <Table<CategoryDto>
            size="small"
            rowKey="id"
            loading={isFetching || (query !== '' && isSearching)}
            dataSource={mains}
            columns={COLUMNS}
            pagination={false}
            rowClassName={(c) => (c.isArchived ? 'row-draft' : '')}
            onRow={selection.rowProps}
          />
        </div>

        <HotkeyHint bindings={selection.bindings} />
      </Flex>
    </FloatingWindow>
  );
}
